import React from 'react';
import { Gamepad2, Mail, Phone, ExternalLink } from 'lucide-react';

const translations = {
  de: {
    contactTitle: 'Kontaktieren Sie uns:',
    contactSubtitle: 'Bitte kontaktieren Sie unser Kundenbetreuungsteam unter.',
    info: 'Info:',
    legal: 'Dies ist ein einmaliger Kauf. Erhalten Sie einen Monat lang unbegrenzten Zugang zum Spieleportal. Die Kosten hierfür betragen 20 Euro.',
    customerService: 'Kundenservice:',
    rights: 'Alle Rechte vorbehalten.',
    quickLinks: 'Schnelllinks',
    home: 'Startseite',
    games: 'Spiele',
    features: 'Vorteile',
    faq: 'FAQ',
  },
  en: {
    contactTitle: 'Contact us:',
    contactSubtitle: 'Please contact our customer support team at.',
    info: 'Info:',
    legal: 'This is a one-time purchase. Get one month of unlimited access to the gaming portal. The cost for this is 20 Euro.',
    customerService: 'Customer Service:',
    rights: 'All rights reserved.',
    quickLinks: 'Quick Links',
    home: 'Home',
    games: 'Games',
    features: 'Features',
    faq: 'FAQ',
  }
};

function Footer({ language }) {
  const t = translations[language];

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer id="footer" className="relative bg-darker border-t border-slate-800">
      {/* Gradient top border */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary to-transparent"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Section */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-6">
              <div className="relative">
                <div className="w-12 h-12 bg-gradient-to-br from-primary via-secondary to-accent rounded-xl flex items-center justify-center">
                  <Gamepad2 className="w-7 h-7 text-white" />
                </div>
              </div>
              <div>
                <span className="font-heading text-xl font-bold text-white tracking-wider">
                  welt<span className="text-accent">welt</span>365
                </span>
                <span className="block text-xs text-muted font-body tracking-widest">.com</span>
              </div>
            </div>
            
            {/* Legal Notice - Prominent */}
            <div className="bg-surface/50 border border-slate-700 rounded-xl p-6 mb-6">
              <p className="text-slate-300 font-body text-base leading-relaxed">
                {t.legal}
              </p>
            </div>

            <p className="text-slate-500 font-body text-sm">
              {t.customerService} <span className="text-accent">de@pixipals.com</span> | <span className="text-accent">08000068268</span>
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-heading font-bold text-white text-lg mb-6">{t.quickLinks}</h3>
            <ul className="space-y-3">
              {[
                { label: t.home, id: 'hero' },
                { label: t.features, id: 'features' },
                { label: t.games, id: 'games' },
                { label: t.faq, id: 'faq' },
              ].map((link) => (
                <li key={link.id}>
                  <button
                    onClick={() => scrollToSection(link.id)}
                    className="text-slate-400 hover:text-white font-body transition-colors duration-200 flex items-center gap-2 group"
                  >
                    <span className="w-1.5 h-1.5 bg-primary rounded-full group-hover:bg-accent transition-colors"></span>
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Section */}
          <div>
            <h3 className="font-heading font-bold text-white text-lg mb-6">{t.contactTitle}</h3>
            <p className="text-slate-400 font-body text-sm mb-6">
              {t.contactSubtitle}
            </p>
            
            <div className="space-y-4">
              <a 
                href="mailto:de@pixipals.com" 
                className="flex items-center gap-3 text-slate-400 hover:text-accent transition-colors group"
              >
                <div className="w-10 h-10 bg-surface border border-slate-700 rounded-lg flex items-center justify-center group-hover:border-accent transition-colors">
                  <Mail className="w-5 h-5" />
                </div>
                <span className="font-body">de@pixipals.com</span>
              </a>
              
              <a 
                href="tel:08000068268" 
                className="flex items-center gap-3 text-slate-400 hover:text-accent transition-colors group"
              >
                <div className="w-10 h-10 bg-surface border border-slate-700 rounded-lg flex items-center justify-center group-hover:border-accent transition-colors">
                  <Phone className="w-5 h-5" />
                </div>
                <span className="font-body">08000068268</span>
              </a>

              <a 
                href="https://mobileinfo.biz" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-slate-400 hover:text-accent transition-colors group"
              >
                <div className="w-10 h-10 bg-surface border border-slate-700 rounded-lg flex items-center justify-center group-hover:border-accent transition-colors">
                  <ExternalLink className="w-5 h-5" />
                </div>
                <span className="font-body">{t.info} mobileinfo.biz</span>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-slate-800">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 font-body text-sm">
              © {new Date().getFullYear()} weltwelt365.com. {t.rights}
            </p>
            
            {/* Decorative element */}
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
              <div className="w-2 h-2 bg-secondary rounded-full animate-pulse animation-delay-200"></div>
              <div className="w-2 h-2 bg-accent rounded-full animate-pulse animation-delay-400"></div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
